import React, { FC, ReactElement, ReactNode, useCallback } from 'react';
import { Button } from 'reactstrap';
import { TranslationKey, useTranslation } from '../translations';
import { useBoolState } from '../utils';
import { captureError } from '../utils/errorHandling';
import { ActionModal } from './ActionModal';

interface ActionButtonProps {
  readonly id: string;
  readonly title: TranslationKey | ReactElement;
  readonly children: ReactNode;
  // Button inside of the modal
  readonly actionContent: TranslationKey;
  // Button the user has to click
  readonly buttonContent: TranslationKey | ReactElement;
  readonly color: 'danger' | 'primary' | 'secondary' | 'success' | 'info';
  readonly block?: boolean;
  readonly outline?: boolean;
  readonly className?: string | undefined;
  readonly style?: React.CSSProperties;
  readonly onComplete: () => Promise<void> | void;
}

export const ActionButtonModal: FC<ActionButtonProps> = ({
  id,
  title,
  children,
  actionContent,
  buttonContent,
  color,
  block,
  outline,
  className,
  style,
  onComplete,
}) => {
  const [isOpen, openModal, closeModal] = useBoolState(false);
  const { ts } = useTranslation();

  const complete = useCallback(async () => {
    try {
      await onComplete();
      closeModal();
    } catch (error) {
      captureError(error as Error);
    }
  }, [onComplete, closeModal]);

  return (
    <>
      <Button
        block={block}
        className={className}
        color={color}
        id={id}
        onClick={openModal}
        outline={outline}
        style={style}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {React.isValidElement<any>(buttonContent) ? buttonContent : ts(buttonContent)}
      </Button>
      <ActionModal
        actionContent={actionContent}
        close={closeModal}
        color={color}
        id={id}
        isOpen={isOpen}
        onComplete={complete}
        title={title}
      >
        {children}
      </ActionModal>
    </>
  );
};
